import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import SGLogo from "@/components/SGLogo";

type NavChild = { label: string; href: string };
type NavLink = { label: string; href: string; heading?: string; children?: NavChild[]; wide?: boolean };

const navLinks: NavLink[] = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  {
    label: "Career Clarity",
    href: "/career-clarity",
    heading: "Career Clarity & Education Pathways",
    wide: true,
    children: [
      { label: "School Students", href: "/career-clarity/stream-selection" },
      { label: "Parents", href: "/career-clarity/career-counseling" },
      { label: "College Students & Graduates", href: "/career-clarity/india-admissions-support" },
      { label: "Working Professionals", href: "/career-clarity/sg-elevate" },
      { label: "Study Abroad", href: "/career-clarity/study-abroad-guidance" },
      { label: "Visa Support", href: "/career-clarity/visitor-dependent-visa-support" },
    ],
  },
  {
    label: "AI Trainings",
    href: "/ai-trainings",
    heading: "AI & Business Enablement",
    wide: true,

    children: [
      { label: "Business Owners — SG Smart Switch", href: "/services/sg-smart-switch" },
      { label: "Corporate Teams — AI Training", href: "/ai-trainings/ai-workshops" },
      { label: "AI Adoption & Consulting", href: "/ai-trainings/ai-adoption-consulting" },
      { label: "Business Workflow Systems", href: "/ai-trainings/business-workflow-systems" },
      { label: "Productivity & Execution", href: "/ai-trainings/productivity-execution-support" },
      { label: "Content & Presentation Training", href: "/ai-trainings/content-presentation-training" },
    ],
  },
  { label: "Workshops", href: "/workshops" },
  { label: "Blogs", href: "/blog" },
  { label: "Contact Us", href: "/contact" },
];

// Routes that DO NOT start with a dark hero — navbar must stay solid white here.
const LIGHT_TOP_ROUTES = [
  "/blog",
  "/enquiry",
  "/registration",
  "/smart-switch-apply",
  "/thank-you",
  "/privacy-policy",
  "/terms-and-conditions",
  "/unsubscribe",
  "/chirag",
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileSub, setOpenMobileSub] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isActive = (href: string) =>
    href === "/" ? location.pathname === "/" : location.pathname.startsWith(href);

  const hasDarkHero = !LIGHT_TOP_ROUTES.some((r) => location.pathname.startsWith(r));
  // Transparent overlay mode only when a dark hero is at the top AND we're at top of page.
  // Mobile menu open forces solid white for legibility.
  const transparent = hasDarkHero && !scrolled && !mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);


  const barH = scrolled ? 52 : 58;
  const logoH = scrolled ? 40 : 48;

  // Color tokens for transparent vs solid mode
  const navTextClass = transparent ? "text-white" : "text-navy";
  const navTextHover = "hover:text-primary";
  const navTextStyle = transparent
    ? { textShadow: "0 1px 12px rgba(0,0,0,0.45)" }
    : undefined;

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-shadow duration-300 ${
          scrolled ? "shadow-elevated" : ""
        }`}
      >
        <div
          className={`relative flex items-stretch transition-colors duration-300 ${
            transparent ? "bg-transparent" : "bg-white"
          }`}
          style={{ height: barH }}
        >

          {/* ── Navy brand zone ── */}
          <Link
            to="/"
            aria-label="Shiksha Guide – Home"
            className="relative z-10 flex shrink-0 items-center bg-navy-deep"
            style={{
              paddingLeft: "clamp(0.75rem, 2vw, 1.5rem)",
              paddingRight: "clamp(1.75rem, 4vw, 3rem)",
              height: barH,
            }}
          >
            <SGLogo height={logoH} dark />

            {/* Curved transition edge */}
            <svg
              className="pointer-events-none absolute top-0"
              style={{ right: -39, height: barH, width: 40 }}
              viewBox={`0 0 40 ${barH}`}
              preserveAspectRatio="none"
              fill="none"
            >
              <path
                d={`M0,0 L0,${barH} C15,${barH} 25,${barH * 0.42} 34,0 Z`}
                className="fill-navy-deep"
              />
            </svg>

            {/* Subtle gold accent line along the curve */}
            <svg
              className="pointer-events-none absolute top-0"
              style={{ right: -40, height: barH, width: 42 }}
              viewBox={`0 0 42 ${barH}`}
              preserveAspectRatio="none"
              fill="none"
            >
              <path
                d={`M0,${barH} C15,${barH} 25,${barH * 0.42} 34,0`}
                stroke="hsl(var(--primary))"
                strokeWidth="1.5"
                strokeOpacity="0.3"
                fill="none"
              />
            </svg>
          </Link>

          {/* ── White navigation zone ── */}
          <div
            className={`flex flex-1 items-center justify-between transition-colors duration-300 ${
              transparent ? "border-b border-transparent" : "border-b border-border"
            }`}
            style={{
              paddingLeft: "clamp(2rem, 4vw, 3.5rem)",
              paddingRight: "clamp(1rem, 3vw, 2.5rem)",
              height: barH,
            }}
          >
            {/* Desktop nav */}
            <nav aria-label="Main navigation" className="hidden items-center gap-0 xl:flex xl:gap-0.5">
              {navLinks.map((link) => {
                const active = isActive(link.href);
                if (link.children) {
                  const isOpen = openDropdown === link.href;
                  return (
                    <div
                      key={link.href}
                      className="relative"
                      ref={isOpen ? dropdownRef : undefined}
                      onMouseEnter={() => setOpenDropdown(link.href)}
                      onMouseLeave={() => setOpenDropdown((cur) => (cur === link.href ? null : cur))}
                    >
                      <div
                        className={`group relative flex items-center text-center text-[13px] font-semibold leading-tight tracking-[0.04em] uppercase transition-colors duration-200 ${
                          active ? "text-primary" : navTextClass
                        }`}
                        style={navTextStyle}
                      >
                        <Link
                          to={link.href}
                          className={`whitespace-nowrap py-2 pl-3.5 pr-1 text-center leading-tight transition-colors ${navTextHover} xl:pl-4 ${
                            active ? "text-primary" : navTextClass
                          }`}
                        >
                          {link.label}
                        </Link>
                        <button
                          type="button"
                          aria-label={`Toggle ${link.label} menu`}
                          aria-expanded={isOpen}
                          onClick={() => setOpenDropdown(isOpen ? null : link.href)}
                          className={`flex items-center py-2 pl-1 pr-3.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${navTextHover} xl:pr-4`}
                        >
                          <ChevronDown
                            size={12}
                            className={`opacity-70 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                        {active && (
                          <span className="absolute bottom-0 left-3.5 right-3.5 h-[2px] rounded-full bg-primary xl:left-4 xl:right-4" />
                        )}
                      </div>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.15 }}
                            className={`absolute left-0 top-full mt-2.5 rounded-xl border border-border bg-white shadow-elevated ${
                              link.wide
                                ? "w-[min(640px,90vw)] p-3"
                                : "w-60 py-2"
                            }`}
                          >
                            {link.heading && (
                              <div className="px-3 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-primary">
                                {link.heading}
                              </div>
                            )}
                            <div className={link.wide ? "grid grid-cols-2 gap-x-2" : ""}>
                              {link.children.map((child) => (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  onClick={() => setOpenDropdown(null)}
                                  className={`block rounded-md px-3 py-2 text-[13px] leading-snug transition-colors hover:bg-muted ${
                                    isActive(child.href) ? "font-semibold text-primary" : "text-body hover:text-navy"
                                  }`}
                                >
                                  {child.label}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    style={navTextStyle}
                    className={`relative whitespace-nowrap px-3.5 py-2 text-center text-[13px] font-semibold leading-tight tracking-[0.04em] uppercase transition-colors duration-200 xl:px-4 ${
                      active ? "text-primary" : `${navTextClass} ${navTextHover}`
                    }`}
                  >
                    {link.label}
                    {active && (
                      <span className="absolute bottom-0 left-3.5 right-3.5 h-[2px] rounded-full bg-primary xl:left-4 xl:right-4" />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden items-center xl:flex">
              <Link
                to="/enquiry"
                className={`group inline-flex items-center gap-2 rounded-full px-5 py-2 text-[13px] font-semibold tracking-wide shadow-button transition-all duration-200 ${
                  transparent
                    ? "bg-gold text-navy hover:bg-gold-soft"
                    : "bg-gold text-navy hover:bg-gold-soft hover:shadow-elevated"
                }`}
              >
                Book a Clarity Call
                <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
              </Link>
            </div>

            {/* Hamburger */}
            <div className="flex flex-1 items-center justify-end xl:hidden">
              <button
                className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-md p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${navTextClass}`}
                style={navTextStyle}
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>

            </div>
          </div>
        </div>


        {/* ── Mobile menu ── */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              id="mobile-nav"
              aria-label="Mobile navigation"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden border-t border-border bg-white px-5 pb-5 xl:hidden"

            >
              {navLinks.map((link) => {
                const active = isActive(link.href);
                if (link.children) {
                  const isOpen = openMobileSub === link.href;
                  return (
                    <div key={link.href} className="border-b border-border/50 last:border-b-0">
                      <div className={`flex items-center justify-between ${active ? "text-primary" : "text-navy"}`}>
                        <Link
                          to={link.href}
                          onClick={() => setMobileOpen(false)}
                          className="flex-1 py-3.5 text-sm font-medium"
                        >
                          {link.label}
                        </Link>
                        <button
                          type="button"
                          aria-label={`Toggle ${link.label} menu`}
                          aria-expanded={isOpen}
                          onClick={() => setOpenMobileSub(isOpen ? null : link.href)}
                          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          <ChevronDown
                            size={16}
                            className={`opacity-60 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                      </div>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden pl-4"
                          >
                            {link.children.map((child) => (
                              <Link
                                key={child.href}
                                to={child.href}
                                onClick={() => setMobileOpen(false)}
                                className={`block py-2.5 text-sm ${isActive(child.href) ? "font-medium text-primary" : "text-body"}`}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`block py-3.5 text-sm font-medium ${active ? "text-primary" : "text-navy"}`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="mt-3 border-t border-border pt-4">
                <Link
                  to="/enquiry"
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-full bg-gold py-3 text-center text-sm font-bold text-navy shadow-button"
                >
                  Book a Clarity Call
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
